import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Paper,
  Avatar,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Apps as AppsIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useApi } from '../../hooks/useApi';
import { getCustomerIdFromSubdomain } from '../../utils/constants';
import { filterInput } from '../../utils/inputSanitization';
import { PROTOCOL_SAML, PROTOCOL_OIDC } from '../../utils/mapperFields';
import CustomApplicationDetailView from './CustomApplicationDetailView';
import ApplicationIcon from './ApplicationIcon';
import RedirectUriListField, {
  parseIndexedFieldErrors,
  validateUriRows,
  WILDCARD_HELPER_TEXT,
  FRAGMENT_HELPER_TEXT,
} from './RedirectUriListField';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../common/ReadOnlyButton';

// iconPreview holds a blob: URL for a freshly picked file. The application's already-uploaded
// icon has no such URL — it has to be fetched with an Authorization header — so this sentinel
// marks "show the stored icon" while keeping iconPreview's truthiness (Change vs Upload label,
// remove button) working exactly as before.
const EXISTING_ICON_PREVIEW = '__existing_icon__';

// Minimalist input style
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: '#27AE60' },
    '&.Mui-focused fieldset': { borderColor: '#27AE60' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: '#27AE60' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

const switchSx = {
  '& .Mui-checked': { color: '#27AE60' },
  '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#27AE60' },
};

const labelSx = { fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' };
const helperSx = { fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' };
const sectionSx = { fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#253E66', textTransform: 'uppercase', letterSpacing: '0.04em' };

// Post-logout redirect URIs are a three-way choice, never a clearable list.
//
// Verified against a live Keycloak 26.7.1: an absent or empty post.logout.redirect.uris
// attribute does NOT mean "no post-logout redirect allowed" -- it means "inherit every
// registered redirect URI", wildcards included. An admin who clears the field expecting to
// lock logout down gets the opposite. So the admin picks a mode and the mode travels on the
// wire as oidc.postLogoutMode; the backend turns it into Keycloak's own encoding (its "-"
// sentinel for none). Never infer the mode from list emptiness.
export const POST_LOGOUT_INHERIT = 'inherit';
export const POST_LOGOUT_EXPLICIT = 'explicit';
export const POST_LOGOUT_NONE = 'none';
const POST_LOGOUT_MODES = [POST_LOGOUT_INHERIT, POST_LOGOUT_EXPLICIT, POST_LOGOUT_NONE];

const POST_LOGOUT_RESERVED_MESSAGE =
  '"+" and "-" are reserved Keycloak values, not URLs. Use the option above to inherit the redirect URIs or to allow none.';

// Wire protocol values are Keycloak's own literals. 'oidc' is accepted as an input alias
// (a stored value could carry it) but is never emitted.
const PROTOCOL_ALIASES = {
  saml: PROTOCOL_SAML,
  oidc: PROTOCOL_OIDC,
  'openid-connect': PROTOCOL_OIDC,
};

/** Anything unrecognised, including nullish, resolves to SAML -- the pre-OIDC default. */
const normalizeProtocol = (protocol) =>
  PROTOCOL_ALIASES[String(protocol || '').toLowerCase()] || PROTOCOL_SAML;

const trimmed = (value) => String(value ?? '').trim();

const cleanUriList = (list) =>
  (Array.isArray(list) ? list : []).map(trimmed).filter(Boolean);

const emptyOidcForm = () => ({
  homeUrl: '',
  redirectUris: [''],
  postLogoutMode: POST_LOGOUT_INHERIT,
  postLogoutRedirectUris: [''],
  publicClient: false,
  // UI-only: maps to pkceMethod on the wire. Always on (and locked) for a public client.
  pkceEnabled: true,
  frontchannelLogout: false,
  frontchannelLogoutUrl: '',
  backchannelLogoutUrl: '',
  backchannelLogoutSessionRequired: false,
  consentRequired: false,
});

const emptyFormData = () => ({
  name: '',
  clientId: '',
  description: '',
  enabled: true,
  protocol: PROTOCOL_SAML,
  // SAML transport fields
  assertionConsumerUrl: '',
  singleLogoutUrl: '',
  nameIdFormat: 'persistent',
  signAssertions: true,
  signResponse: true,
  clientSignature: false,
  relayState: '',
  // OIDC fields, nested to mirror the request body
  oidc: emptyOidcForm(),
});

const emptyRowErrors = () => ({ redirectUris: {}, postLogoutRedirectUris: {} });

const resolvePostLogoutMode = (mode) =>
  POST_LOGOUT_MODES.includes(mode) ? mode : POST_LOGOUT_INHERIT;

/**
 * The Client ID field's helper text, which is not the same advice for both protocols.
 *
 * A SAML client ID is an entity ID and is very often a URL. An OIDC client ID can never be:
 * the backend enforces `^[a-zA-Z0-9._-]{1,120}$` with an alphanumeric first character, so
 * `https://…` is rejected on the scheme's colon and slashes. One shared "identifier or URL"
 * line invited half the tenants who read it to type something the API refuses.
 */
const clientIdHelperText = (isOidc) =>
  isOidc
    ? 'Letters, numbers, dot, underscore and hyphen only — not a URL (cannot be changed after creation)'
    : 'Unique identifier or URL, e.g. a SAML entity ID (cannot be changed after creation)';

/**
 * Turn form state into the request body for the ACTIVE protocol only.
 *
 * The form necessarily holds both protocols' fields, but the request must not: posting the
 * inactive half means every SAML create and update ships an `oidc` object of empty defaults,
 * which the backend would have to guess at and which would then be stamped onto the app in
 * the UI. So a SAML body carries no `oidc` key at all, and an OIDC body carries none of the
 * SAML transport fields.
 *
 * This is a faithful serializer, not a validator -- `validateFormData` is the gate that stops
 * a contradictory pair (explicit mode with an empty list) from being submitted. If one is
 * built anyway the backend rejects it with a 400, which is loud rather than silent.
 */
export const buildRequestBody = (formData) => {
  const protocol = normalizeProtocol(formData.protocol);
  const common = {
    name: formData.name,
    clientId: formData.clientId,
    description: formData.description,
    enabled: formData.enabled,
    protocol,
  };

  if (protocol === PROTOCOL_SAML) {
    return {
      ...common,
      assertionConsumerUrl: formData.assertionConsumerUrl,
      singleLogoutUrl: formData.singleLogoutUrl,
      nameIdFormat: formData.nameIdFormat,
      signAssertions: formData.signAssertions,
      signResponse: formData.signResponse,
      clientSignature: formData.clientSignature,
      relayState: formData.relayState,
    };
  }

  const form = formData.oidc || {};
  const postLogoutMode = resolvePostLogoutMode(form.postLogoutMode);

  const oidc = {
    homeUrl: trimmed(form.homeUrl),
    redirectUris: cleanUriList(form.redirectUris),
    // Always sent explicitly. The backend has a fallback for non-UI API clients; relying on
    // it here is how "none" would silently become "inherit every redirect URI".
    postLogoutMode,
    publicClient: !!form.publicClient,
    frontchannelLogout: !!form.frontchannelLogout,
    backchannelLogoutSessionRequired: !!form.backchannelLogoutSessionRequired,
    consentRequired: !!form.consentRequired,
  };

  if (postLogoutMode === POST_LOGOUT_EXPLICIT) {
    oidc.postLogoutRedirectUris = cleanUriList(form.postLogoutRedirectUris);
  }

  // Prism forces S256 for public clients and the admin cannot turn it off; a confidential
  // client may opt in. 'plain' is never offered, at either end.
  if (form.publicClient || form.pkceEnabled) {
    oidc.pkceMethod = 'S256';
  }

  // A front-channel URL without the toggle on has nothing to trigger it, so it is not sent.
  if (form.frontchannelLogout && trimmed(form.frontchannelLogoutUrl)) {
    oidc.frontchannelLogoutUrl = trimmed(form.frontchannelLogoutUrl);
  }
  if (trimmed(form.backchannelLogoutUrl)) {
    oidc.backchannelLogoutUrl = trimmed(form.backchannelLogoutUrl);
  }

  return { ...common, oidc };
};

/** Returns an error message to show the admin, or null when the form may be submitted. */
export const validateFormData = (formData) => {
  const protocol = normalizeProtocol(formData.protocol);

  if (!trimmed(formData.name) || !trimmed(formData.clientId)) {
    return 'Name and Client ID are required';
  }

  if (protocol === PROTOCOL_SAML) {
    return trimmed(formData.assertionConsumerUrl)
      ? null
      : 'Assertion Consumer Service URL is required for a SAML application';
  }

  const form = formData.oidc || {};

  if (!trimmed(form.homeUrl)) return 'Home URL is required for an OpenID Connect application';
  if (cleanUriList(form.redirectUris).length === 0) return 'At least one redirect URI is required';

  const redirectRowError = validateUriRows(form.redirectUris).find(Boolean);
  if (redirectRowError) return redirectRowError;

  if (resolvePostLogoutMode(form.postLogoutMode) === POST_LOGOUT_EXPLICIT) {
    if (cleanUriList(form.postLogoutRedirectUris).length === 0) {
      return 'Add at least one post-logout redirect URI, or choose "Same as redirect URIs" or "None allowed"';
    }
    const postLogoutRowError = validateUriRows(form.postLogoutRedirectUris, {
      reservedMessage: POST_LOGOUT_RESERVED_MESSAGE,
    }).find(Boolean);
    if (postLogoutRowError) return postLogoutRowError;
  }

  return null;
};

/**
 * Rebuild form state from a saved application. The post-logout mode is read from the app when
 * the backend supplies it; the fallback below only ever runs against a record written before
 * the field existed, and errs towards Keycloak's own default (inherit) for an empty list.
 */
const formDataFromApp = (app) => {
  const protocol = normalizeProtocol(app.protocol);
  const base = {
    ...emptyFormData(),
    name: app.name,
    clientId: app.clientId,
    description: app.description || '',
    enabled: app.enabled,
    protocol,
    assertionConsumerUrl: app.assertionConsumerUrl || '',
    singleLogoutUrl: app.singleLogoutUrl || '',
    nameIdFormat: app.nameIdFormat || 'persistent',
    signAssertions: app.signAssertions,
    signResponse: app.signResponse,
    clientSignature: app.clientSignature,
    relayState: app.relayState || '',
  };

  if (protocol !== PROTOCOL_OIDC) return base;

  const oidc = app.oidc || {};
  const postLogoutList = Array.isArray(oidc.postLogoutRedirectUris) ? oidc.postLogoutRedirectUris : [];
  const postLogoutMode = POST_LOGOUT_MODES.includes(oidc.postLogoutMode)
    ? oidc.postLogoutMode
    : (postLogoutList.length > 0 ? POST_LOGOUT_EXPLICIT : POST_LOGOUT_INHERIT);

  return {
    ...base,
    oidc: {
      homeUrl: oidc.homeUrl || '',
      redirectUris: Array.isArray(oidc.redirectUris) && oidc.redirectUris.length ? [...oidc.redirectUris] : [''],
      postLogoutMode,
      postLogoutRedirectUris: postLogoutList.length ? [...postLogoutList] : [''],
      publicClient: !!oidc.publicClient,
      pkceEnabled: !!oidc.publicClient || oidc.pkceMethod === 'S256',
      frontchannelLogout: !!oidc.frontchannelLogout,
      frontchannelLogoutUrl: oidc.frontchannelLogoutUrl || '',
      backchannelLogoutUrl: oidc.backchannelLogoutUrl || '',
      backchannelLogoutSessionRequired: !!oidc.backchannelLogoutSessionRequired,
      consentRequired: !!oidc.consentRequired,
    },
  };
};

const CustomApplicationConfig = forwardRef((props, ref) => {
  const confirm = useConfirm();
  const isReadOnly = useReadOnlyMode();
  const {
    customApplications,
    createCustomApplication,
    fetchCustomApplications,
    updateCustomApplication,
    deleteCustomApplication,
    uploadCustomApplicationIcon,
    deleteCustomApplicationIcon,
    loading,
  } = useApi();

  // List/detail toggle state
  const [selectedApp, setSelectedApp] = useState(null);

  // Create/Edit dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [uriRowErrors, setUriRowErrors] = useState(emptyRowErrors);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /*
   * Why the list keeps its own error rather than reading one off the page.
   *
   * "This tenant has no applications" and "the list could not be read" are the same empty
   * array, and this page used to render the same "No custom applications configured" alert for
   * both. That is not a cosmetic difference. An admin whose Keycloak is unreachable, or whose
   * token has expired, was being told their applications DO NOT EXIST; the natural next move is
   * to create one, which then collides with the client that is still there and turns a five
   * second outage into a phantom-duplicate hunt. Absent data is UNKNOWN, never empty — the same
   * distinction the OIDC connection panel had to make for a configuration it could not read.
   *
   * Local state, not useApi's `error`: that one is shared by every call the hook makes and
   * clears itself after three seconds, so a failed SAVE would put this list into "could not be
   * loaded" and a real load failure would quietly stop being one while still on screen.
   */
  const [listError, setListError] = useState(null);

  /*
   * Which submission the dialog currently belongs to.
   *
   * handleSubmit awaits twice (the save, then the refetch) and then writes state, and the
   * dialog can be closed underneath it in between. Every submission takes a token from this
   * counter; anything that invalidates the dialog the submission was started from — the admin
   * closing it — bumps the counter, and every post-await write checks its token first.
   * Without it, closing mid-save and starting a NEW application let the old save's completion
   * handler close the new dialog, or pin the old save's row errors onto the new form.
   */
  const submissionRef = useRef(0);

  const customerId = getCustomerIdFromSubdomain();

  const oidcForm = formData.oidc || emptyOidcForm();
  const isOidc = normalizeProtocol(formData.protocol) === PROTOCOL_OIDC;
  const postLogoutMode = resolvePostLogoutMode(oidcForm.postLogoutMode);

  const setOidc = (patch) =>
    setFormData((prev) => ({ ...prev, oidc: { ...(prev.oidc || emptyOidcForm()), ...patch } }));

  useImperativeHandle(ref, () => ({
    openAddDialog: () => setShowDialog(true),
    isDetailView: () => selectedApp !== null,
  }));

  /*
   * The list fetch, wrapped so that nothing can reject out of it.
   *
   * Its two callers — the mount effect and the Retry button — are both places where a rejection
   * has nowhere to go: React drops the effect's promise on the floor, so the failure became an
   * unhandled rejection and the page went on to describe the tenant as empty. The whole body
   * sits inside the try for that reason.
   */
  const loadApplications = async () => {
    if (!customerId) return;
    try {
      await fetchCustomApplications(customerId);
      setListError(null);
    } catch (error) {
      console.error('Failed to load custom applications:', error);
      // Same surface as a failed save or delete: useApi keeps its own `error` state that this
      // component never reads, so the toast is the only thing the admin actually sees — and
      // the banner below is what stops the page from claiming an answer it does not have.
      const message = error?.message || 'Failed to load the applications';
      setListError(message);
      toast.error(message);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [customerId]);

  // Notify parent when selectedApp changes (for hiding page header elements)
  useEffect(() => {
    if (props.onDetailViewChange) {
      props.onDetailViewChange(selectedApp !== null);
    }
  }, [selectedApp]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const validationError = validateFormData(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    // Claim the dialog. Every state write below an await is gated on still holding it.
    const token = (submissionRef.current += 1);
    const isCurrent = () => submissionRef.current === token;

    setUriRowErrors(emptyRowErrors());
    setSubmitting(true);
    const requestBody = buildRequestBody(formData);
    try {
      let appId;
      if (editingApp) {
        await updateCustomApplication(customerId, editingApp.id, requestBody);
        appId = editingApp.id;
      } else {
        const result = await createCustomApplication(customerId, requestBody);
        appId = result?.id;
      }
      // Upload icon if a file was selected
      if (iconFile && appId) {
        try {
          await uploadCustomApplicationIcon(customerId, appId, iconFile);
        } catch (iconError) {
          console.error('Failed to upload icon:', iconError);
          if (isCurrent()) {
            toast.warning('Application saved but icon upload failed');
          }
        }
      }
      // Everything past here belongs to the dialog this submission was started from. Nothing
      // is lost by abandoning it: useApi's own create/update already refetched the list, so
      // the only casualty is this component's extra refresh for icon changes.
      if (!isCurrent()) return;
      const savedApp = editingApp;
      // resetDialogState, not handleCloseDialog: closing the dialog because the save succeeded
      // must not invalidate the very submission that is doing it.
      resetDialogState();
      // Refresh to pick up icon changes. The returned list is used directly rather than the
      // `customApplications` state read: this closure captured that array before the save, so
      // it still holds the pre-edit record.
      let latestApps = null;
      if (customerId) {
        latestApps = await fetchCustomApplications(customerId);
        // The list just came back from the server, so a banner left over from an earlier
        // failed load is describing an outage that is over. Leaving it up would keep telling
        // the admin this list is unreliable while they are looking at a fresh copy of it.
        setListError(null);
      }
      if (!isCurrent()) return;
      // If we were editing the currently selected app, update it from the server's own copy.
      // Overlaying form state here would put back exactly the fields buildRequestBody strips.
      if (savedApp && selectedApp && savedApp.id === selectedApp.id) {
        const source = Array.isArray(latestApps) ? latestApps : customApplications;
        const refreshed = source.find(a => a.id === savedApp.id);
        if (refreshed) {
          setSelectedApp(refreshed);
        }
      }
    } catch (error) {
      console.error('Failed to save custom application:', error);
      // A failure belonging to a dialog the admin already closed has nothing to report
      // against: its row errors would land on whatever form is open now, and its toast would
      // name an application the admin has moved on from.
      if (!isCurrent()) return;
      // OIDC validation failures come back indexed (`redirectUris[2]`), but api.js flattens the
      // response to a message string, so recover the index and pin the text to its row.
      const message = error?.message || '';
      const rowErrors = {
        redirectUris: parseIndexedFieldErrors(message, 'redirectUris'),
        postLogoutRedirectUris: parseIndexedFieldErrors(message, 'postLogoutRedirectUris'),
      };
      setUriRowErrors(rowErrors);
      /*
       * Everything that is not a per-row URI failure has no row to sit against:
       * clientId/name/homeUrl/pkceMethod validation, the protocol- and publicClient-
       * immutable 409s, a duplicate clientId, any 500. useApi keeps its own `error` state
       * that this component never reads, so without this the dialog simply sat there
       * unchanged and the admin saw nothing happen at all.
       *
       * Only when nothing was pinned: a toast repeating text already shown against the
       * offending row is noise, and parseIndexedFieldErrors' contract is that the caller
       * surfaces the raw message when it returns nothing.
       */
      const pinnedCount =
        Object.keys(rowErrors.redirectUris).length +
        Object.keys(rowErrors.postLogoutRedirectUris).length;
      if (pinnedCount === 0) {
        toast.error(message || 'Failed to save the application');
      }
    } finally {
      // Only the submission that still owns the dialog may release it. A superseded one
      // clearing the flag would re-enable Create underneath a save that is still running;
      // an abandoned one is released by handleCloseDialog instead.
      if (isCurrent()) {
        setSubmitting(false);
      }
    }
  };

  const handleEdit = async (app) => {
    setEditingApp(app);
    setFormData(formDataFromApp(app));
    setUriRowErrors(emptyRowErrors());
    setIconFile(null);
    // The already-stored icon has no URL the browser can load directly (authenticated,
    // different origin), so the sentinel stands in for it and ApplicationIcon fetches it.
    setIconPreview(app.hasIcon ? EXISTING_ICON_PREVIEW : null);
    setShowDialog(true);
  };

  /*
   * The delete never reported anything. The await below had no catch, so a rejection was an
   * unhandled promise React drops on the floor: the OIDC delete is authoritative, and when
   * Keycloak cannot be reached the backend contains the client and answers 502 with the state
   * it left behind — disabled with a rotated secret, or still live. That is precisely the
   * message an operator has to act on, and it was the message being discarded.
   *
   * The whole flow sits inside the try so no path out of this handler can reject: it is wired
   * straight to onClick, where nothing is left to catch it.
   */
  const handleDelete = async (app) => {
    try {
      const confirmed = await confirm({
        title: 'Delete Application',
        message: `Are you sure you want to delete "${app.name}"?`,
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) return;

      await deleteCustomApplication(customerId, app.id);
      // Return to list view if deleting the currently viewed app. Only on success: the
      // backend keeps the reference row on a containment failure so the delete can be retried
      // from the UI that reported it, and leaving the detail view would claim the opposite.
      if (selectedApp && selectedApp.id === app.id) {
        setSelectedApp(null);
      }
    } catch (error) {
      console.error('Failed to delete custom application:', error);
      // Same surface as a failed save: useApi keeps its own `error` state that this component
      // never reads, so a toast is the only thing the admin actually sees.
      toast.error(error?.message || 'Failed to delete the application');
    }
  };

  // Clear the dialog WITHOUT disowning the submission in flight. Used by the success path,
  // which is closing the dialog on behalf of that very submission.
  const resetDialogState = () => {
    setShowDialog(false);
    setEditingApp(null);
    setFormData(emptyFormData());
    setUriRowErrors(emptyRowErrors());
    setIconFile(null);
    setIconPreview(null);
  };

  // The admin-facing close. Bumping the counter is what makes any save still running belong
  // to nobody, so its completion cannot reach into whatever the admin opens next. `submitting`
  // is released here too — the abandoned save's own finally block declines to.
  const handleCloseDialog = () => {
    submissionRef.current += 1;
    setSubmitting(false);
    resetDialogState();
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('Icon file must be less than 512KB');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPEG, SVG, and WebP images are allowed');
      return;
    }
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleRemoveIcon = async () => {
    if (editingApp && editingApp.hasIcon && !iconFile) {
      try {
        await deleteCustomApplicationIcon(customerId, editingApp.id);
        // Awaited so a failed refresh lands in the catch below. Un-awaited it was a second
        // unhandled rejection escaping this file, sitting right next to the one in the mount
        // effect: the try block it appears to be inside cannot catch a promise it never waits on.
        if (customerId) await fetchCustomApplications(customerId);
      } catch (error) {
        console.error('Failed to delete icon:', error);
      }
    }
    setIconFile(null);
    setIconPreview(null);
  };

  /*
   * `loading` is one flag shared by every call this hook instance makes, so it is true during
   * a create just as much as during the first list fetch. This branch used to return the
   * spinner ALONE, and renderDialog() was only reached from the two branches below it — which
   * meant creating a tenant's very first application (empty list, loading true) unmounted the
   * dialog in the middle of its own save. The dialog renders into a portal, so keeping it here
   * costs nothing visually and keeps it on screen for the one case that needs it most.
   */
  if (loading && customApplications.length === 0) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress sx={{ color: '#27AE60' }} />
        </Box>
        {renderDialog()}
      </>
    );
  }

  // Detail view
  if (selectedApp) {
    // Find the latest version of the app from the list (in case it was updated)
    const currentApp = customApplications.find(a => a.id === selectedApp.id) || selectedApp;
    return (
      <>
        <CustomApplicationDetailView
          app={currentApp}
          onBack={() => setSelectedApp(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Create/Edit Application Dialog (shared) */}
        {renderDialog()}
      </>
    );
  }

  // List view
  return (
    <Paper elevation={0} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
      <Box sx={{ p: 3 }}>
        {/*
          Three answers, never two. The spinner branch above owns "still asking"; these own
          "asked and the answer is none" and "asked and could not get an answer", which used to
          be the same screen. The failure banner is deliberately not written as a verdict about
          the tenant, and it carries the retry that re-asks the question — reloading the whole
          page was the only way back before.

          The two are not mutually exclusive: a refresh can fail against a list that already
          loaded, in which case the rows stay on screen underneath the banner rather than
          vanishing, because they are still the last thing the server actually said.
        */}
        {listError && (
          <Alert
            severity="error"
            data-testid="custom-applications-load-error"
            action={
              <Button
                onClick={loadApplications}
                size="small"
                sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#E74C3C' }}
              >
                Retry
              </Button>
            }
            sx={{
              mb: customApplications.length > 0 ? 1.5 : 0,
              alignItems: 'center',
              // Same palette the disabled-application chip uses, so the failure reads as part
              // of this page rather than as MUI's default red.
              backgroundColor: '#FDF2F2',
              border: '1px solid #E74C3C20',
              '& .MuiAlert-icon': { color: '#E74C3C' },
            }}
          >
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66' }}>
              Your applications could not be loaded, so this is not a list of what your tenant
              has — there may well be applications configured. Nothing has been changed or lost
              and sign-in is unaffected. Retry once the identity server is reachable again.
              {' ('}{listError}{')'}
            </Typography>
          </Alert>
        )}

        {!listError && customApplications.length === 0 && (
          <Alert severity="info" sx={{ backgroundColor: '#EBF5FB', border: '1px solid #4698D320', '& .MuiAlert-icon': { color: '#4698D3' } }}>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66' }}>
              No custom applications configured. Click "Add Application" to create one.
            </Typography>
          </Alert>
        )}

        {customApplications.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {customApplications.map((app, index) => (
              <Box
                key={app.id}
                onClick={() => setSelectedApp(app)}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: '#FFFFFF',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#27AE60',
                    backgroundColor: '#FCFCFC',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {/*
                    The uploaded icon is deliberately NOT rendered here. An application's own
                    branding belongs to the end user's application-selection page on the proxy,
                    where it is the thing being picked; this is an admin inventory, and a
                    uniform mark keeps the rows scannable by name rather than by logo. The
                    icon is still managed from the create/edit dialog, which does show it.
                  */}
                  <Avatar sx={{ backgroundColor: '#27AE60', width: 36, height: 36, fontSize: '0.9rem', flexShrink: 0 }}>
                    <AppsIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#253E66' }}>
                        {app.name}
                      </Typography>
                      <Chip
                        label={normalizeProtocol(app.protocol) === PROTOCOL_OIDC ? 'OIDC' : 'SAML'}
                        size="small"
                        sx={{
                          backgroundColor: '#F4F6F8',
                          color: '#253E66',
                          border: '1px solid #E5E5E5',
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Chip
                        label={app.enabled ? 'Enabled' : 'Disabled'}
                        size="small"
                        sx={{
                          backgroundColor: app.enabled ? '#F0F9F4' : '#FDF2F2',
                          color: app.enabled ? '#27AE60' : '#E74C3C',
                          border: `1px solid ${app.enabled ? '#27AE6020' : '#E74C3C20'}`,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                    {app.description && (
                      <Typography sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.85rem',
                        color: '#999999',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {app.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <ChevronRightIcon sx={{ color: '#CCCCCC', fontSize: 22, flexShrink: 0, ml: 1 }} />
              </Box>
            ))}
          </Box>
        )}

        {/* Create/Edit Application Dialog */}
        {renderDialog()}
      </Box>
    </Paper>
  );

  function renderProtocolField() {
    // Protocol is fixed at creation: Keycloak stores a client under one protocol and the
    // backend rejects a change, so on edit this is an indicator rather than a control.
    if (editingApp) {
      return (
        <Box>
          <Typography sx={labelSx}>Protocol</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={isOidc ? 'OpenID Connect' : 'SAML 2.0'}
              size="small"
              sx={{
                backgroundColor: '#F4F6F8',
                color: '#253E66',
                border: '1px solid #E5E5E5',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                height: 24,
                fontSize: '0.75rem',
              }}
            />
            <Typography sx={helperSx}>Cannot be changed after creation.</Typography>
          </Box>
        </Box>
      );
    }
    return (
      <FormControl>
        <FormLabel sx={labelSx}>Protocol</FormLabel>
        <RadioGroup
          row
          name="protocol"
          value={normalizeProtocol(formData.protocol)}
          onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
        >
          <FormControlLabel
            value={PROTOCOL_SAML}
            control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#27AE60' } }} />}
            label={<Typography sx={labelSx}>SAML 2.0</Typography>}
          />
          <FormControlLabel
            value={PROTOCOL_OIDC}
            control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#27AE60' } }} />}
            label={<Typography sx={labelSx}>OpenID Connect</Typography>}
          />
        </RadioGroup>
      </FormControl>
    );
  }

  function renderSamlFields() {
    return (
      <>
        <TextField name="assertionConsumerUrl" label="Assertion Consumer Service URL" value={formData.assertionConsumerUrl} onChange={(e) => setFormData({ ...formData, assertionConsumerUrl: filterInput(e.target.value, 'URL') })} required fullWidth size="small" sx={inputStyle} />
        <TextField name="singleLogoutUrl" label="Single Logout URL (Optional)" value={formData.singleLogoutUrl} onChange={(e) => setFormData({ ...formData, singleLogoutUrl: filterInput(e.target.value, 'URL') })} fullWidth size="small" sx={inputStyle} />
        <TextField name="relayState" label="IDP Initiated SSO Relay State (Optional)" value={formData.relayState} onChange={(e) => setFormData({ ...formData, relayState: filterInput(e.target.value, 'URL') })} fullWidth size="small" helperText="URL to redirect users to after SAML authentication" sx={inputStyle} />
        <TextField name="nameIdFormat" label="Name ID Format" value={formData.nameIdFormat} onChange={handleChange} select fullWidth size="small" sx={inputStyle}>
          <MenuItem value="persistent">Persistent</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="unspecified">Unspecified</MenuItem>
        </TextField>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel control={<Switch checked={formData.signAssertions} onChange={handleChange} name="signAssertions" sx={switchSx} />} label={<Typography sx={labelSx}>Sign Assertions</Typography>} />
          <FormControlLabel control={<Switch checked={formData.signResponse} onChange={handleChange} name="signResponse" sx={switchSx} />} label={<Typography sx={labelSx}>Sign Response</Typography>} />
          <FormControlLabel control={<Switch checked={formData.clientSignature} onChange={handleChange} name="clientSignature" sx={switchSx} />} label={<Typography sx={labelSx}>Client Signature Required</Typography>} />
        </Box>
      </>
    );
  }

  function renderOidcFields() {
    return (
      <>
        <TextField
          name="homeUrl"
          label="Home URL"
          value={oidcForm.homeUrl}
          onChange={(e) => setOidc({ homeUrl: filterInput(e.target.value, 'URL') })}
          required
          fullWidth
          size="small"
          helperText="Where the portal tile sends the user, e.g. https://app.example.com/. Must be an absolute https:// URL — no wildcards."
          sx={inputStyle}
        />

        <RedirectUriListField
          label="Redirect URI"
          value={oidcForm.redirectUris}
          onChange={(redirectUris) => setOidc({ redirectUris })}
          rowErrors={uriRowErrors.redirectUris}
          required
          addLabel="Add redirect URI"
          helperText={`Where Prism may return the user after sign-in. At least one is required. ${WILDCARD_HELPER_TEXT} ${FRAGMENT_HELPER_TEXT}`}
          sx={inputStyle}
        />

        <Typography sx={helperSx}>
          Browser (CORS) web origins are derived automatically from these redirect URIs. There is nothing to configure,
          and any value sent by a client is ignored.
        </Typography>

        <FormControl>
          <FormLabel sx={labelSx}>Post-logout redirect URIs</FormLabel>
          <Typography sx={helperSx}>
            An empty list is not "none" in OIDC — it means inherit every redirect URI, wildcards included. Choose explicitly.
          </Typography>
          <RadioGroup
            name="postLogoutMode"
            value={postLogoutMode}
            onChange={(e) => setOidc({ postLogoutMode: e.target.value })}
          >
            <FormControlLabel
              value={POST_LOGOUT_INHERIT}
              control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#27AE60' } }} />}
              label={<Typography sx={labelSx}>Same as redirect URIs</Typography>}
            />
            <FormControlLabel
              value={POST_LOGOUT_EXPLICIT}
              control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#27AE60' } }} />}
              label={<Typography sx={labelSx}>Specific URIs</Typography>}
            />
            <FormControlLabel
              value={POST_LOGOUT_NONE}
              control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#27AE60' } }} />}
              label={<Typography sx={labelSx}>None allowed</Typography>}
            />
          </RadioGroup>
          {postLogoutMode === POST_LOGOUT_EXPLICIT && (
            <Box sx={{ mt: 1 }}>
              <RedirectUriListField
                label="Post-logout redirect URI"
                value={oidcForm.postLogoutRedirectUris}
                onChange={(postLogoutRedirectUris) => setOidc({ postLogoutRedirectUris })}
                rowErrors={uriRowErrors.postLogoutRedirectUris}
                required
                addLabel="Add post-logout redirect URI"
                reservedMessage={POST_LOGOUT_RESERVED_MESSAGE}
                placeholder="https://app.example.com/signed-out"
                helperText={`At least one is required. ${WILDCARD_HELPER_TEXT} ${FRAGMENT_HELPER_TEXT}`}
                sx={inputStyle}
              />
            </Box>
          )}
        </FormControl>

        <Typography sx={sectionSx}>Client type</Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={oidcForm.publicClient}
                onChange={(e) => setOidc({ publicClient: e.target.checked })}
                name="publicClient"
                disabled={!!editingApp}
                sx={switchSx}
              />
            }
            label={<Typography sx={labelSx}>Public client — SPA / mobile app (no client secret)</Typography>}
          />
          <Typography sx={helperSx}>
            {editingApp
              ? 'Cannot be changed after creation. Register a new application if the client type changes.'
              : 'Leave off for a server-side application that can keep a client secret.'}
          </Typography>
        </Box>

        {oidcForm.publicClient ? (
          <TextField
            label="PKCE method"
            value="S256"
            fullWidth
            size="small"
            disabled
            slotProps={{ input: { readOnly: true } }}
            helperText="Prism enforces PKCE with S256 for public clients. This cannot be disabled, and 'plain' is never accepted."
            sx={inputStyle}
          />
        ) : (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!oidcForm.pkceEnabled}
                  onChange={(e) => setOidc({ pkceEnabled: e.target.checked })}
                  name="pkceEnabled"
                  sx={switchSx}
                />
              }
              label={<Typography sx={labelSx}>Require PKCE (S256)</Typography>}
            />
            <Typography sx={helperSx}>Optional for a confidential client, but recommended.</Typography>
          </Box>
        )}

        <Typography sx={sectionSx}>Logout</Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={oidcForm.frontchannelLogout}
                onChange={(e) => setOidc({ frontchannelLogout: e.target.checked })}
                name="frontchannelLogout"
                sx={switchSx}
              />
            }
            label={<Typography sx={labelSx}>Front-channel logout</Typography>}
          />
          <Typography sx={helperSx}>Loads a URL in the user's browser at sign-out so the application can clear its session.</Typography>
        </Box>
        {oidcForm.frontchannelLogout && (
          <TextField
            label="Front-channel logout URL"
            value={oidcForm.frontchannelLogoutUrl}
            onChange={(e) => setOidc({ frontchannelLogoutUrl: filterInput(e.target.value, 'URL') })}
            fullWidth
            size="small"
            helperText="Absolute https:// URL. No wildcards."
            sx={inputStyle}
          />
        )}
        <TextField
          label="Back-channel logout URL (Optional)"
          value={oidcForm.backchannelLogoutUrl}
          onChange={(e) => setOidc({ backchannelLogoutUrl: filterInput(e.target.value, 'URL') })}
          fullWidth
          size="small"
          helperText="Called server-to-server at sign-out. More reliable than front-channel logout."
          sx={inputStyle}
        />
        <FormControlLabel
          control={
            <Switch
              checked={oidcForm.backchannelLogoutSessionRequired}
              onChange={(e) => setOidc({ backchannelLogoutSessionRequired: e.target.checked })}
              name="backchannelLogoutSessionRequired"
              sx={switchSx}
            />
          }
          label={<Typography sx={labelSx}>Include session ID in the back-channel logout token</Typography>}
        />

        <FormControlLabel
          control={
            <Switch
              checked={oidcForm.consentRequired}
              onChange={(e) => setOidc({ consentRequired: e.target.checked })}
              name="consentRequired"
              sx={switchSx}
            />
          }
          label={<Typography sx={labelSx}>Require user consent at first sign-in</Typography>}
        />
      </>
    );
  }

  /*
   * onClose is withheld while a save is in flight, which is how MUI is told to ignore the
   * backdrop and Escape, and Cancel is disabled alongside it. Those are the ACCIDENTAL closes
   * — a stray click outside a wide dialog, a reflexive Escape — and they throw away a form the
   * admin cannot get back. The title-bar X stays live as the deliberate way out, made safe by
   * submissionRef rather than taken away.
   */
  function renderDialog() {
    return (
      <Dialog open={showDialog} onClose={submitting ? undefined : handleCloseDialog} maxWidth="md" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F0F9F4', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}><AppsIcon /></Avatar>
          {editingApp ? 'Edit Application' : 'Create Application'}
          {/*
            The one close route left open during a save, and deliberately so: an admin who has
            decided to walk away from a request that is hanging needs a way out. It is safe
            because handleCloseDialog disowns the submission — see submissionRef.
          */}
          <IconButton aria-label="Close" onClick={handleCloseDialog} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderProtocolField()}
              <TextField name="name" label="Application Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: filterInput(e.target.value, 'GENERAL_TEXT') })} required fullWidth size="small" sx={inputStyle} />
              <TextField name="clientId" label="Client ID" value={formData.clientId} onChange={(e) => setFormData({ ...formData, clientId: filterInput(e.target.value, 'URL') })} required disabled={!!editingApp} fullWidth size="small" helperText={clientIdHelperText(isOidc)} sx={inputStyle} />
              <TextField name="description" label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: filterInput(e.target.value, 'GENERAL_TEXT') })} multiline rows={2} fullWidth size="small" sx={inputStyle} />
              <Box>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666', mb: 1 }}>Application Icon (optional)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {iconPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      {iconPreview === EXISTING_ICON_PREVIEW ? (
                        <ApplicationIcon
                          key={`${editingApp?.id}:${editingApp?.updatedAt || ''}`}
                          customerId={customerId}
                          applicationId={editingApp?.id}
                          hasIcon
                          version={editingApp?.updatedAt}
                          variant="rounded"
                          sx={{ width: 48, height: 48, border: '1px solid #E5E5E5' }}
                        >
                          <ImageIcon />
                        </ApplicationIcon>
                      ) : (
                        <Avatar src={iconPreview} variant="rounded" sx={{ width: 48, height: 48, border: '1px solid #E5E5E5' }}>
                          <ImageIcon />
                        </Avatar>
                      )}
                      <IconButton size="small" onClick={handleRemoveIcon} sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff', border: '1px solid #E5E5E5', width: 20, height: 20, '&:hover': { backgroundColor: '#fee' } }}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 48, height: 48, backgroundColor: '#F0F4F8', border: '1px dashed #E5E5E5' }}>
                      <ImageIcon sx={{ color: '#999' }} />
                    </Avatar>
                  )}
                  <ReadOnlyButton variant="outlined" component={isReadOnly ? undefined : "label"} startIcon={<CloudUploadIcon />} size="small" sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px', '&:hover': { borderColor: '#27AE60', color: '#27AE60' } }}>
                    {iconPreview ? 'Change Icon' : 'Upload Icon'}
                    {!isReadOnly && <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleIconChange} />}
                  </ReadOnlyButton>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999' }}>PNG, JPEG, SVG, WebP. Max 512KB.</Typography>
                </Box>
              </Box>
              {isOidc ? renderOidcFields() : renderSamlFields()}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={formData.enabled} onChange={handleChange} name="enabled" sx={switchSx} />} label={<Typography sx={labelSx}>Enabled</Typography>} />
              </Box>
            </Box>
            </motion.div>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5E5', gap: 1.5 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Cancel</Button>
            <ReadOnlyButton type="submit" variant="contained" disabled={submitting || loading} sx={{ backgroundColor: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#219A52' } }}>{editingApp ? 'Update' : 'Create'}</ReadOnlyButton>
          </DialogActions>
        </form>
      </Dialog>
    );
  }
});

export default CustomApplicationConfig;
